import { RespawnPointKind, TICK_MS } from "odyssey-shared";
import type { CueName } from "../cues.ts";
import { say } from "../../words.ts";

/**
 * The death screen's state (ADR-073's amendment "the respawn is the player's, and a death is said in chat", decision
 * 8): shown from the player's own death until `vitals` says it stands again, with the cause the `death` event named,
 * the time the respawn may be asked for and the point the button names from the server's `respawnOffer`, and the time
 * of the last request, and with what the death cost (P2.5-T7): the penalties the owner's `effects` frame of the
 * death's tick carries (`deathCosts`). A cause, an offer or a cost that arrives before the dead `vitals` is kept and
 * applied as the screen shows. The time is one tick past the server's, so a page whose clock runs ahead never asks before the respawn tick. The button is enabled once that time has come and no request waits; a request waits until the
 * respawn closes the screen or `RESPAWN_HOLD_MS` has passed. A death seen live fades the screen in and fires the
 * `player_death` cue; a character the page joins dead shows it at once and fires nothing.
 */

/**
 * How long a request holds the button before it may be pressed again, should the respawn not arrive.
 */
export const RESPAWN_HOLD_MS = 2000;

/**
 * A point as the offer names it.
 */
export interface OfferedPoint {
  readonly kind: string;
  readonly name: string;
}

/**
 * The words a point is named by on the button: its name, else its kind's (a kind this build does not know is named
 * as the spawn is).
 *
 * @param point The point.
 * @returns The place.
 */
export function pointPlace(point: OfferedPoint): string {
  if (point.name.trim() !== "") {
    return point.name.trim();
  }
  return point.kind === RespawnPointKind.Shrine ? say("death.screen.shrine") : say("death.screen.safePlace");
}

/**
 * What the screen shows: a serial that moves at each death, whether it fades in, the cause line, the cost lines (none
 * before the frame that carries them, or for a death that brought no penalty), when the respawn may be asked for in
 * page time (null before the offer), the place the button names (null before the offer), and when the last request went
 * (null for none).
 */
export interface DeathShown {
  readonly serial: number;
  readonly fade: boolean;
  readonly cause: string | null;
  readonly costs: readonly string[];
  readonly readyAtMs: number | null;
  readonly place: string | null;
  readonly requestedAtMs: number | null;
}

/**
 * Whether the button may be pressed at a moment: the offer has come, its time has come, and no request holds it.
 *
 * @param shown The screen.
 * @param nowMs Page time in ms.
 * @returns True when enabled.
 */
export function respawnReady(shown: DeathShown, nowMs: number): boolean {
  return shown.readyAtMs !== null && nowMs >= shown.readyAtMs && (shown.requestedAtMs === null || nowMs - shown.requestedAtMs >= RESPAWN_HOLD_MS);
}

/**
 * The whole seconds the countdown shows at a moment, rounded up, counted to the server's time rather than the button's,
 * which is a tick later, so a 5-second delay never reads 6.
 *
 * @param shown The screen.
 * @param nowMs Page time in ms.
 * @returns The seconds, 0 once the server's time has come or before the offer.
 */
export function secondsLeft(shown: DeathShown, nowMs: number): number {
  return shown.readyAtMs === null ? 0 : Math.max(0, Math.ceil((shown.readyAtMs - TICK_MS - nowMs) / 1000));
}

/**
 * No cost lines: a death that brought no penalty, or one whose frame has not come.
 */
const NO_COSTS: readonly string[] = Object.freeze([]);

/**
 * Whether two lists of lines read the same, so a frame that repeats the lines shown tells no listener.
 *
 * @param a One list.
 * @param b The other.
 * @returns True when each line equals its counterpart.
 */
function sameLines(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((line, index) => line === b[index]);
}

/**
 * The screen's store, which the overlay reads and the session and the keys drive.
 */
export class DeathScreen {
  private shown: DeathShown | null = null;
  private serial = 0;
  private pendingCause: string | null = null;
  private pendingCosts: readonly string[] = NO_COSTS;
  private standFrameDue = false;
  private pendingOffer: { readonly readyAtMs: number; readonly place: string } | null = null;
  private send: ((point: number) => boolean) | null = null;
  private readonly listeners = new Set<() => void>();

  /**
   * Whether the overlay draws its button enabled now: the overlay writes it each time it renders, so a reading of the
   * screen (the page's report) says what the player sees, which is a render after the moment `respawnReady` turns.
   */
  public drawnReady = false;

  /**
   * @param cue Fires a named cue (`cues.ts`).
   */
  public constructor(private readonly cue: (name: CueName) => void) {}

  /**
   * Adds a listener for a changed screen.
   *
   * @param listener Called after each change.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * What the screen shows now.
   *
   * @returns The screen, or null while the character lives.
   */
  public readonly getSnapshot = (): DeathShown | null => this.shown;

  /**
   * Hands the screen the way to send `respawn` on the current connection.
   *
   * @param send Sends a request for a point; false when it could not be sent.
   */
  public bindRespawn(send: (point: number) => boolean): void {
    this.send = send;
  }

  /**
   * The player's own character died, as its `vitals` say: shows the screen once, fading in and firing `player_death`
   * for a death seen live.
   *
   * @param live False for a character the page joined dead.
   */
  public died(live: boolean): void {
    if (this.shown !== null) {
      return;
    }
    this.serial += 1;
    const offer = this.pendingOffer;
    this.set({ serial: this.serial, fade: live, cause: this.pendingCause, costs: this.pendingCosts, readyAtMs: offer?.readyAtMs ?? null, place: offer?.place ?? null, requestedAtMs: null });
    this.pendingCause = null;
    this.pendingCosts = NO_COSTS;
    this.pendingOffer = null;
    this.standFrameDue = false;
    if (live) {
      this.cue("player_death");
    }
  }

  /**
   * The `death` event naming the player's own character: its cause line, kept for a screen not yet shown.
   *
   * @param line The death line (`deathLine`).
   */
  public cause(line: string): void {
    if (this.shown === null) {
      this.pendingCause = line;
      return;
    }
    this.set({ ...this.shown, cause: line });
  }

  /**
   * The cost lines of the owner's latest `effects` frame (`deathCosts`), told at every frame: shown at once while the
   * character lies dead, else kept for a screen not yet shown, which serves a dead `vitals` the transport delivered a
   * tick after its frame. The first frame after a stand is dropped: it is the frame of the tick the character stood up
   * in, which follows that tick's `vitals` and lists the penalty at its full time again, and its lines are the last
   * death's, never the next one's.
   *
   * @param lines The lines, none for a frame that holds no penalty at its full time.
   */
  public cost(lines: readonly string[]): void {
    if (this.shown === null) {
      this.pendingCosts = this.standFrameDue ? NO_COSTS : lines;
      this.standFrameDue = false;
      return;
    }
    if (!sameLines(lines, this.shown.costs)) {
      this.set({ ...this.shown, costs: lines });
    }
  }

  /**
   * The server's `respawnOffer`: when the respawn may be asked for, one tick past the ticks it names so the page never
   * asks before the server allows, and the point the button names, the first; kept for a screen not yet shown.
   *
   * @param readyTicks Ticks until it may be asked for, counted from the tick it was sent.
   * @param points The points, the default first.
   * @param nowMs Page time when it arrived.
   */
  public offer(readyTicks: number, points: readonly OfferedPoint[], nowMs: number): void {
    const first = points[0];
    if (first === undefined) {
      return;
    }
    const offer = { readyAtMs: nowMs + (readyTicks + 1) * TICK_MS, place: pointPlace(first) };
    if (this.shown === null) {
      this.pendingOffer = offer;
      return;
    }
    this.set({ ...this.shown, ...offer, requestedAtMs: null });
  }

  /**
   * Presses the button, by pointer or key: sends `respawn` for the first point when it is enabled.
   *
   * @param nowMs Page time in ms.
   * @returns True when a request was sent.
   */
  public press(nowMs: number): boolean {
    const shown = this.shown;
    if (shown === null || !respawnReady(shown, nowMs) || this.send === null || !this.send(0)) {
      return false;
    }
    this.set({ ...shown, requestedAtMs: nowMs });
    return true;
  }

  /**
   * The player's own character stands again, as its `vitals` say: the screen closes, and the frame of this tick is
   * awaited so its lines are not kept (`cost`). A `vitals` of a character that was not lying dead only drops what was
   * kept for a screen.
   */
  public stood(): void {
    this.drawnReady = false;
    this.forget();
    if (this.shown !== null) {
      this.standFrameDue = true;
      this.set(null);
    }
  }

  /**
   * Forgets everything, as a session's end must: the screen, what was kept for it, and the stand.
   */
  public reset(): void {
    const wasShown = this.shown !== null;
    this.drawnReady = false;
    this.forget();
    this.standFrameDue = false;
    if (wasShown) {
      this.set(null);
    }
  }

  /**
   * Drops the cause, the cost lines and the offer kept for a screen not yet shown.
   */
  private forget(): void {
    this.pendingCause = null;
    this.pendingCosts = NO_COSTS;
    this.pendingOffer = null;
  }

  /**
   * Sets the screen and tells the listeners.
   *
   * @param shown The new screen.
   */
  private set(shown: DeathShown | null): void {
    this.shown = shown;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
